/**
 * INSPECT IA REVIEW — modelo da revisão automática de relatórios.
 *
 * A IA IDENTIFICA E AUXILIA; O PROFISSIONAL DECIDE. Nada aqui aprova
 * relatório, altera número, inventa norma ou resultado. A revisão produz
 * FINDINGS por campo (`fieldPath`), e só as correções classificadas como
 * SAFE_LANGUAGE_CHANGE podem ser aplicadas sem decisão caso a caso.
 */

/** Documentos que passam pela revisão. Um tipo novo entra com um adaptador, não com lógica nova. */
export type AIReviewSubjectType = "ME" | "LP" | "PM" | "USC" | "BSCAN" | "AVR" | "RDO" | "INTEGRITY";

export const AI_REVIEW_SUBJECT_LABELS: Readonly<Record<AIReviewSubjectType, string>> = {
  ME: "Medição de Espessura (ME)",
  LP: "Líquido Penetrante (LP)",
  PM: "Partículas Magnéticas (PM)",
  USC: "Ultrassom Convencional (US)",
  BSCAN: "B-Scan / C-Scan",
  AVR: "AVR — Vida Remanescente (API 570)",
  RDO: "RDO",
  INTEGRITY: "Relatório de Integridade (RI)"
};

/** Nível do finding — decide o que pode ser automático e o que bloqueia. */
export type AIFindingSeverity = "INFO" | "LINGUISTICO" | "TERMINOLOGICO" | "TECNICO" | "CRITICO";

export const AI_SEVERITY_LABELS: Readonly<Record<AIFindingSeverity, string>> = {
  INFO: "INFO", LINGUISTICO: "LINGUÍSTICO", TERMINOLOGICO: "TERMINOLÓGICO", TECNICO: "TÉCNICO", CRITICO: "CRÍTICO"
};

/** Área do painel "INSPEÇÃO AUTOMÁTICA IA". */
export type AIFindingCategory =
  | "ORTOGRAFIA"
  | "GRAMATICA"
  | "TERMINOLOGIA"
  | "COERENCIA"
  | "RESULTADOS_CONCLUSAO"
  | "NORMAS"
  | "UNIDADES"
  | "VALIDACAO";

export const AI_CATEGORY_LABELS: Readonly<Record<AIFindingCategory, string>> = {
  ORTOGRAFIA: "ORTOGRAFIA",
  GRAMATICA: "GRAMÁTICA",
  TERMINOLOGIA: "TERMINOLOGIA",
  COERENCIA: "COERÊNCIA",
  RESULTADOS_CONCLUSAO: "RESULTADOS × CONCLUSÃO",
  NORMAS: "NORMAS",
  UNIDADES: "UNIDADES",
  VALIDACAO: "VALIDAÇÃO DETERMINÍSTICA"
};

/** Ordem das linhas do painel. */
export const AI_CATEGORY_ORDER: readonly AIFindingCategory[] = [
  "ORTOGRAFIA", "GRAMATICA", "TERMINOLOGIA", "COERENCIA", "RESULTADOS_CONCLUSAO", "NORMAS", "UNIDADES", "VALIDACAO"
];

/**
 * OPEN        aguardando decisão
 * APPLIED     sugestão aplicada ao campo (antes/depois guardados)
 * EDITED      a pessoa escreveu o próprio texto no lugar
 * REJECTED    descartada, com motivo opcional
 * ACKNOWLEDGED alerta técnico aceito como válido — ainda precisa ser CORRIGIDO
 * JUSTIFIED   alerta técnico mantido com justificativa escrita
 */
export type AIFindingStatus = "OPEN" | "APPLIED" | "EDITED" | "REJECTED" | "ACKNOWLEDGED" | "JUSTIFIED";

export const AI_FINDING_STATUS_LABELS: Readonly<Record<AIFindingStatus, string>> = {
  OPEN: "PENDENTE", APPLIED: "APLICADA", EDITED: "EDITADA", REJECTED: "REJEITADA", ACKNOWLEDGED: "ACEITA — CORRIGIR", JUSTIFIED: "JUSTIFICADA"
};

export interface AISourceRef {
  readonly document: string;
  readonly revision: string;
  readonly section: string;
  readonly entryId: string | null;
}

export interface AIReviewFinding {
  readonly id: string;
  readonly reviewId: string;
  readonly subjectType: AIReviewSubjectType;
  readonly reportId: string;
  readonly revision: string;
  /** Campo de origem ("comments", "results[2].description", "conclusion.observation"). */
  readonly fieldPath: string;
  readonly fieldLabel: string;
  readonly category: AIFindingCategory;
  readonly severity: AIFindingSeverity;
  /** Trecho do campo a que o finding se refere. Vazio quando é sobre o relatório inteiro. */
  readonly originalText: string;
  /** Sugestão — vazia quando não há texto a propor (ex.: incoerência). */
  readonly suggestedText: string;
  readonly explanation: string;
  readonly ruleId: string;
  readonly sourceRefs: readonly AISourceRef[];
  /** SAFE_LANGUAGE_CHANGE: pode ser aplicada sem decisão caso a caso. */
  readonly autoApplicable: boolean;
  /** Alerta técnico pode ser mantido com justificativa (TÉCNICO sim; CRÍTICO e validação não). */
  readonly justifiable: boolean;
  /** Posição do trecho no campo quando revisado (-1 = sem posição). */
  readonly offset: number;
  status: AIFindingStatus;
  resolutionNote: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
}

/**
 * NOT_STARTED / RUNNING / PASSED / WARNING / REQUIRES_ACTION / FAILED — e
 * STALE quando o relatório mudou depois da revisão (hash diferente).
 */
export type AIReviewStatus =
  | "AI_REVIEW_NOT_STARTED"
  | "AI_REVIEW_RUNNING"
  | "AI_REVIEW_PASSED"
  | "AI_REVIEW_WARNING"
  | "AI_REVIEW_REQUIRES_ACTION"
  | "AI_REVIEW_FAILED"
  | "AI_REVIEW_STALE";

export const AI_REVIEW_STATUS_LABELS: Readonly<Record<AIReviewStatus, string>> = {
  AI_REVIEW_NOT_STARTED: "NÃO EXECUTADA",
  AI_REVIEW_RUNNING: "EM EXECUÇÃO",
  AI_REVIEW_PASSED: "PASSOU",
  AI_REVIEW_WARNING: "PASSOU COM ALERTAS",
  AI_REVIEW_REQUIRES_ACTION: "REQUER REVISÃO",
  AI_REVIEW_FAILED: "FALHOU",
  AI_REVIEW_STALE: "DESATUALIZADA"
};

export type AIReviewJobStatus = "QUEUED" | "PROCESSING" | "COMPLETED" | "FAILED";

export interface AIReportReview {
  readonly id: string;
  readonly subjectType: AIReviewSubjectType;
  readonly reportId: string;
  readonly reportNumber: string;
  readonly reportRevision: string;
  readonly clientId: string;
  status: AIReviewStatus;
  jobStatus: AIReviewJobStatus;
  /** Hash do conteúdo revisado — se o relatório mudar, a revisão fica DESATUALIZADA. */
  contentHash: string;
  technicalStatus: "OK" | "ALERTAS" | "INCONSISTENCIAS";
  findingsCount: number;
  criticalCount: number;
  warningCount: number;
  /** Correções linguísticas seguras aplicadas automaticamente nesta revisão. */
  autoAppliedCount: number;
  modelProvider: string;
  modelName: string;
  modelVersion: string;
  promptVersion: string;
  failureReason: string;
  startedAt: string;
  completedAt: string | null;
  requestedBy: string;
  requestedByName: string;
}

/** Alteração aplicada a um campo — o texto anterior NUNCA se perde. Append-only. */
export interface AIAppliedChange {
  readonly id: string;
  readonly subjectType: AIReviewSubjectType;
  readonly reportId: string;
  readonly fieldPath: string;
  readonly findingId: string | null;
  readonly reviewId: string | null;
  readonly kind: "AUTO_SAFE" | "ACCEPTED" | "MANUAL_EDIT" | "ASSISTANT" | "REVERT";
  readonly before: string;
  readonly after: string;
  readonly userId: string;
  readonly at: string;
}

// ═══════════════════════════════════════════════════════════ decisão do status

const isOpen = (f: AIReviewFinding) => f.status === "OPEN" || f.status === "ACKNOWLEDGED";

/**
 * O status sai dos findings — nunca de uma nota. A IA não aprova: ela diz
 * PASSOU, PASSOU COM ALERTAS ou REQUER REVISÃO.
 *   crítico aberto, técnico não resolvido ou validação pendente → REQUER REVISÃO
 *   terminológico/info/linguístico pendente ou técnico justificado → COM ALERTAS
 */
export function deriveReviewStatus(findings: readonly AIReviewFinding[]): { status: AIReviewStatus; technicalStatus: AIReportReview["technicalStatus"] } {
  const blocking = findings.some(f => isOpen(f) && (f.severity === "CRITICO" || f.severity === "TECNICO"));
  if (blocking) return { status: "AI_REVIEW_REQUIRES_ACTION", technicalStatus: "INCONSISTENCIAS" };
  const warnings = findings.some(f => isOpen(f) || f.status === "JUSTIFIED");
  if (warnings) return { status: "AI_REVIEW_WARNING", technicalStatus: findings.some(f => f.status === "JUSTIFIED") ? "ALERTAS" : "OK" };
  return { status: "AI_REVIEW_PASSED", technicalStatus: "OK" };
}

export function countFindings(findings: readonly AIReviewFinding[]): { findingsCount: number; criticalCount: number; warningCount: number } {
  return {
    findingsCount: findings.length,
    criticalCount: findings.filter(f => f.severity === "CRITICO" && isOpen(f)).length,
    warningCount: findings.filter(f => isOpen(f) && f.severity !== "CRITICO").length
  };
}

// ═══════════════════════════════════════════════════════════ gate de envio

export interface AISubmitPolicy {
  /** Exigir revisão IA antes de ENVIAR PARA ANÁLISE (legado / consultivo). */
  readonly requireReviewBeforeSubmit?: boolean;
  /** Provedor indisponível (legado / consultivo). */
  readonly offlinePolicy?: "BLOCK" | "ALLOW_WITH_RECORD";
}

export type AISubmitDecision =
  | { readonly allowed: true; readonly note: string }
  | { readonly allowed: false; readonly reason: string };

/**
 * Parecer consultivo da IA para o envio ao PLH.
 * 100% CONSULTIVO: sempre retorna allowed: true, pois a IA auxilia e sinaliza,
 * mas a autoridade e a decisão de envio pertencem exclusivamente ao profissional.
 */
export function evaluateSubmitGate(
  review: AIReportReview | null,
  findings: readonly AIReviewFinding[],
  currentHash: string,
  _policy?: AISubmitPolicy
): AISubmitDecision {
  if (!review) {
    return { allowed: true, note: "Revisão IA ainda não executada." };
  }
  if (review.status === "AI_REVIEW_RUNNING") {
    return { allowed: true, note: "Revisão IA em execução. O envio continua disponível." };
  }
  if (review.status === "AI_REVIEW_FAILED") {
    return { allowed: true, note: `INSPECT IA REVIEW indisponível (${review.failureReason || "falha"}). A análise profissional pode prosseguir.` };
  }
  if (review.contentHash !== currentHash) {
    return { allowed: true, note: "O documento foi alterado depois da última revisão IA. Recomenda-se executar nova revisão." };
  }
  const critical = findings.filter(f => isOpen(f) && f.severity === "CRITICO");
  if (critical.length > 0) {
    return { allowed: true, note: `A IA identificou ${critical.length} inconsistência(s) crítica(s): ${critical[0]!.explanation}` };
  }
  const technical = findings.filter(f => isOpen(f) && f.severity === "TECNICO");
  if (technical.length > 0) {
    return { allowed: true, note: `A IA identificou ${technical.length} alerta(s) técnico(s). Recomenda-se revisão pelo profissional.` };
  }
  return { allowed: true, note: review.status === "AI_REVIEW_WARNING" ? "Revisão IA: passou com alertas." : "Revisão IA: passou sem alertas." };
}
